import React, { useEffect, useState } from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  HoverCardArrow,
} from "@/components/ui/hover-card";
import { Bot } from "@/components/icons";
import { Close } from "@castlery/fortress/Icons";
import { useChatStore } from "@/stores/chatStore";
import { apiPost } from "@/utils/apiHelper";
import { PROACTIVE_API } from "@/config/api";
import {
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  trackEvent,
} from "@/utils/analytics";
import { getCachedDeviceInfo } from "@/utils/deviceInfo";
import { ProactiveMessage } from "@/types";
import { cn } from "@/lib/utils";
import Markdown from "react-markdown";
import { Link } from "@/components/fortress";
import { throwCasaApiError } from "@/utils";
import { useSafeRemarkPlugins } from "@/hooks/useSafeRemarkPlugins";

interface ProactiveGreetingProps {
  enable: boolean;
  children: React.ReactNode;
}

export const ProactiveGreeting: React.FC<ProactiveGreetingProps> = ({
  enable,
  children,
}) => {
  const remarkPlugins = useSafeRemarkPlugins();
  const { market, userHandledProactiveGreeting, setCurrentView, openChat } =
    useChatStore();

  const [open, setOpen] = useState(false);
  const [proactiveGreeting, setProactiveGreeting] =
    useState<ProactiveMessage | null>(null);

  useEffect(() => {
    if (!enable) return;

    const fetchProactiveMessage = async () => {
      try {
        const deviceInfo = getCachedDeviceInfo();
        const response = await apiPost<{ data: ProactiveMessage }>(
          PROACTIVE_API.get(),
          {
            current_url: window.location.href,
            market: market,
            device_info: {
              browser: {
                name: deviceInfo?.browser.name,
                version: deviceInfo?.browser.version,
                ua: deviceInfo?.userAgent,
              },
              network: deviceInfo?.connection?.effectiveType,
              os: deviceInfo?.platform,
              device_type: deviceInfo?.deviceType,
            },
          },
        );
        setProactiveGreeting(response.data);
      } catch (error) {
        // 使用默认消息
        setProactiveGreeting({
          text: "Hello! I'm Casa. How can I help you?",
        });
        // throwCasaApiError(error);
      } finally {
        setOpen(true);
      }
    };

    fetchProactiveMessage();
  }, [enable]);

  const handleStartChat = () => {
    apiPost(PROACTIVE_API.click(), {
      current_url: window.location.href,
      text: proactiveGreeting?.text,
      is_agree: true,
    });

    // 追踪 Proactive Greeting 交互
    trackEvent({
      event: TRACK_EVENT,
      category: "greeting",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "chat_now",
    });

    // 切换到 ChatBox 视图并展开聊天窗口
    setCurrentView("chatbox");
    openChat();

    setOpen(false);
  };

  const handleRejectChat = () => {
    apiPost(PROACTIVE_API.click(), {
      current_url: window.location.href,
      text: proactiveGreeting?.text,
      is_agree: false,
    });

    // 追踪 Proactive Greeting 交互
    trackEvent({
      event: TRACK_EVENT,
      category: "greeting",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "close",
    });

    userHandledProactiveGreeting();

    setOpen(false);
  };

  return (
    <HoverCard
      open={enable && open}
      // onOpenChange={() => setOpen(true)}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="left"
        align="end"
        className={cn("bg-[#D25C1B] border-[#D25C1B] rounded-none shadow-lg", {
          "w-[calc(100vw-20px-10px-70px)] max-w-[320px] min-w-[280px] ": true,
          "sm:w-[320px] sm:min-w-[280px]": true,
        })}
      >
        <div className="space-y-2">
          <div className="flex items-start justify-between space-x-2">
            <div className="w-4 h-4 bg-[#D25C1B] rounded-full">
              <Bot />
            </div>
            <div className="flex-1 text-white">
              <Markdown
                remarkPlugins={remarkPlugins}
                components={{
                  a: ({ href, children }) => (
                    <Link
                      variant="primary"
                      color="white"
                      size="xs"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </Link>
                  ),
                  p: ({ children, ...props }) => (
                    <p className="first:mt-0 mt-[10px]" {...props}>
                      {children}
                    </p>
                  ),
                  li: ({ children, ...props }) => (
                    <li className="list-disc ml-5" {...props}>
                      {children}
                    </li>
                  ),
                }}
              >
                {proactiveGreeting?.text}
              </Markdown>
            </div>
            <div className="cursor-pointer" onClick={handleRejectChat}>
              <Close
                className="w-4 h-4"
                sx={{
                  fill: "white",
                }}
              />
            </div>
          </div>

          <div
            className="text-right text-lg text-white font-light underline cursor-pointer"
            onClick={handleStartChat}
          >
            Chat now
          </div>
        </div>
        <HoverCardArrow className="fill-[#D25C1B]" width={16} height={8} />
      </HoverCardContent>
    </HoverCard>
  );
};
